import type { ICampaignGroup } from '~/modules/campaign-group';
import type { TemplateStatusInfo } from '../types';
import type { Campaign } from '~/types/graphql';

import { useUnpublishedThemePages } from './useUnpublishedThemePages';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useThemePageStore } from '~/stores/themePage';
import { useShopifyAsset } from '~/modules/shopify';
import { determineTemplateStatus } from '../utils';
import { useAssetsStore } from '~/stores/assets';
import { ETemplateStatus } from '../types';
import { useMemo } from 'react';

export interface IUseTemplateStatusProps {
  campaign?: Campaign | ICampaignGroup;
  enabled?: boolean;
}

export interface IUseTemplateStatusResult {
  deletedTemplates: TemplateStatusInfo[];
  unpublishedTemplates: TemplateStatusInfo[];
  hasDeleted: boolean;
  hasUnpublished: boolean;
  hasUnpublishedAndDeleted: boolean;
  hasUnpublishedOrDeleted: boolean;
  allVariantsHaveIssues: boolean;
}

const EMPTY_RESULT: IUseTemplateStatusResult = {
  deletedTemplates: [],
  unpublishedTemplates: [],
  hasDeleted: false,
  hasUnpublished: false,
  hasUnpublishedAndDeleted: false,
  hasUnpublishedOrDeleted: false,
  allVariantsHaveIssues: false,
};

export const useTemplateStatus = ({
  campaign,
  enabled = true,
}: IUseTemplateStatusProps): IUseTemplateStatusResult => {
  const assets = useAssetsStore((state) => state.assets);

  const allThemePagesMap = useThemePageStore((state) => state.allThemePagesMap);
  const { isFetching: isShopifyAssetFetching } = useShopifyAsset();
  const { isFetchingUnpublishedThemePages } = useUnpublishedThemePages();

  const allAssets = useMemo(() => {
    if (!enabled || !assets) return [];

    return [...assets.templateGPV6, ...assets.templateGPV7, ...assets.templateShopify];
  }, [assets, enabled]);

  const campaigns: Campaign[] = useMemo(() => {
    if (!campaign) return [];

    if (campaign.type === 'TEMPLATE_BUNDLE' && 'campaigns' in campaign)
      return campaign.campaigns ?? [];

    return [campaign] as Campaign[];
  }, [campaign]);

  const { templates, variantsWithIssuesCount, totalValidVariants } = useMemo(() => {
    if (
      !enabled ||
      isShopifyAssetFetching ||
      isFetchingUnpublishedThemePages ||
      campaign?.status === CAMPAIGN_STATUS.STOPPED
    ) {
      return {
        templates: [] as TemplateStatusInfo[],
        variantsWithIssuesCount: 0,
        totalValidVariants: 0,
      };
    }

    const results: TemplateStatusInfo[] = [];
    const variantIdsWithIssues = new Set<string>();
    let validVariantsCount = 0;

    for (const campaign of campaigns) {
      if (!campaign?.variants || campaign.deletedAt) continue;

      const isRunning = campaign.status === CAMPAIGN_STATUS.RUNNING;

      for (const variant of campaign.variants) {
        if (!variant?.templateSuffix) continue;
        validVariantsCount++;

        const { status, label } = determineTemplateStatus({
          templateSuffix: variant.templateSuffix,
          assets: allAssets,
          themePages: allThemePagesMap,
        });

        if (status === ETemplateStatus.NORMAL) continue;

        // For UNPUBLISHED status: only check control (isOrigin) when campaign is not running
        if (status === ETemplateStatus.UNPUBLISHED && !isRunning && !variant.isOrigin) {
          continue;
        }

        variantIdsWithIssues.add(variant.id);
        results.push({
          variant,
          campaign,
          status,
          name: `${label}`,
        });
      }
    }

    return {
      templates: results,
      variantsWithIssuesCount: variantIdsWithIssues.size,
      totalValidVariants: validVariantsCount,
    };
  }, [
    campaigns,
    allAssets,
    allThemePagesMap,
    isShopifyAssetFetching,
    isFetchingUnpublishedThemePages,
    campaign?.status,
    enabled,
  ]);

  const deletedTemplates = useMemo(() => {
    return templates.filter((t) => t.status === ETemplateStatus.DELETED);
  }, [templates]);

  const unpublishedTemplates = useMemo(() => {
    return templates.filter((t) => t.status === ETemplateStatus.UNPUBLISHED);
  }, [templates]);

  if (!enabled) return EMPTY_RESULT;

  const hasDeleted = deletedTemplates.length > 0;
  const hasUnpublished = unpublishedTemplates.length > 0;
  const hasUnpublishedAndDeleted = hasDeleted && hasUnpublished;
  const hasUnpublishedOrDeleted = hasDeleted || hasUnpublished;
  const allVariantsHaveIssues =
    totalValidVariants > 0 && variantsWithIssuesCount === totalValidVariants;

  return {
    deletedTemplates,
    unpublishedTemplates,
    hasDeleted,
    hasUnpublished,
    hasUnpublishedAndDeleted,
    hasUnpublishedOrDeleted,
    allVariantsHaveIssues,
  };
};
